import React, { useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { ff } from '../../../../theme';

interface HomeSectionProps {
  title: string;
  itemCount: number;
  collapsible?: boolean;
  storageKey?: string;
  /** Collapsible header count pill in ink/lime instead of grey (board: the urgent first group). */
  highlight?: boolean;
  children: React.ReactNode;
}

const PREVIEW_COUNT = 3;

const HomeSection: React.FC<HomeSectionProps> = ({ title, itemCount, collapsible = false, storageKey, highlight = false, children }) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (!storageKey) return true;
    const saved = sessionStorage.getItem(`home_section_${storageKey}`);
    return saved === null ? true : saved === 'true';
  });
  const [showAll, setShowAll] = useState(false);

  if (itemCount === 0) return null;

  const canSeeAll = itemCount > PREVIEW_COUNT;
  const childArray = React.Children.toArray(children);
  const visibleChildren = showAll ? childArray : childArray.slice(0, PREVIEW_COUNT);

  const seeAllButton = canSeeAll && (
    <Box
      as="button"
      type="button"
      h="44px"
      display="flex"
      alignItems="center"
      fontSize="14px"
      fontWeight={600}
      color={ff.ink}
      flex="none"
      onClick={() => setShowAll(prev => !prev)}
    >
      {showAll ? 'Show less' : 'See all'}
    </Box>
  );

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (storageKey) sessionStorage.setItem(`home_section_${storageKey}`, String(next));
  };

  return (
    <Box as="section" display="flex" flexDirection="column" px="20px">
      {collapsible ? (
        <Box as="h2" m="16px 0 0 0">
          <Box
            as="button"
            type="button"
            aria-expanded={isOpen}
            onClick={toggle}
            w="100%"
            h="52px"
            borderBottom="1px solid"
            borderColor={ff.line}
            display="flex"
            alignItems="center"
            gap="10px"
            color={ff.ink}
            textAlign="left"
          >
            <Text as="span" flex={1} fontSize="17px" fontWeight={600}>
              {title}
            </Text>
            <Flex
              as="span"
              minW="24px"
              h="24px"
              px="7px"
              borderRadius="full"
              align="center"
              justify="center"
              fontSize="12px"
              fontWeight={600}
              bg={highlight ? ff.ink : '#F0F0EE'}
              color={highlight ? ff.lime : ff.body}
            >
              {itemCount}
            </Flex>
            <Box as="span" flex="none" transition="transform 0.15s" transform={`rotate(${isOpen ? 180 : 0}deg)`}>
              <FiChevronDown size={18} strokeWidth={2.4} aria-hidden />
            </Box>
          </Box>
        </Box>
      ) : (
        <Flex align="center" justify="space-between" minH="44px" mt="16px">
          <Text as="h2" m={0} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2}>
            {title} · {itemCount}
          </Text>
          {seeAllButton}
        </Flex>
      )}
      {isOpen && (
        <Flex direction="column">
          {visibleChildren}
          {collapsible && seeAllButton}
        </Flex>
      )}
    </Box>
  );
};

export default HomeSection;
